import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Helmet } from "react-helmet";
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '../../config/config';
import RobotSelection from '../Common/RobotSelection';
import FolderSelection from '../Common/FolderSelection';
import ProcessSelection from '../Common/ProcessSelection';
import '../../styles/Triggers.css';


const WhatsAppTrigger = () => {
    const [name, setName] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [robot, setRobot] = useState('');
    const [folder, setFolder] = useState('');
    const [process, setProcess] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [formErrors, setFormErrors] = useState({});
    const [priority, setPriority] = useState("Inherited");
    const [stickToRobot, setStickToRobot] = useState(false);
    const [argumentsList, setArgumentsList] = useState([]);
    const navigate = useNavigate();

    const priorities = [
        { value: "Inherited", label: "Inherited", icon: "○" },
        { value: "Critical", label: "Critical", icon: "◊" },
        { value: "Highest", label: "Highest", icon: "≫" },
        { value: "High", label: "High", icon: "∧" },
        { value: "Medium", label: "Medium", icon: "=" },
        { value: "Low", label: "Low", icon: "∨" },
        { value: "Lowest", label: "Lowest", icon: "≪" }
    ];

    const validateForm = () => {
        const errors = {};
        if (!name.trim()) errors.name = "Trigger name is required";
        if (!phoneNumber.trim()) errors.phoneNumber = "WhatsApp number is required";
        if (!folder) errors.folder = "Folder selection is required";
        if (!process) errors.process = "Process selection is required"; // Simplified validation

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Add validation for phone number
    // const validatePhoneNumber = (number) => {
    //     const phoneRegex = /^(\+[1-9]\d{1,14})(,\+[1-9]\d{1,14})*$/; // Updated regex for multiple numbers
    //     return phoneRegex.test(number);
    // };
    const validatePhoneNumber = (number) => {
        const phoneNumbers = number.split(',').map(num => num.trim());
        const phoneRegex = /^\+[1-9]\d{9,14}$/;

        const invalids = phoneNumbers.filter(num => !phoneRegex.test(num));

        if (invalids.length > 0) {
            toast.error(`Invalid phone number(s): ${invalids.join(', ')}`);
            return false;
        }

        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Add phone number validation
        // if (!validatePhoneNumber(phoneNumber)) {
        //     toast.error('Please enter a valid phone number with country code');
        //     return;
        // }

        if (!validateForm()) {
            toast.error('Please fill in all required fields');
            return;
        }

        setIsLoading(true);
        try {
            const requestData = {
                name,
                status: "Active",
                createdBy: localStorage.getItem("userId"),
                whatsappConfig: {
                    phone_number: phoneNumber,
                    message_filter: 'whatsapp',
                    process: process?.key || process?.id || null,
                    robot_id: robot,
                    folder_id: folder,
                    priority,
                    stickto_robot: stickToRobot
                },
                folderId: folder
            };

            const response = await axios.post(
                `${config.API_BASE_URL}/api/triggers/whatsapp`,
                requestData,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("userToken")}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            toast.success('WhatsApp trigger created successfully');
            navigate('/dashboard');
        } catch (error) {
            console.error('Error creating WhatsApp trigger:', error);
            const errorMessage = error.response?.data?.error ||
                error.response?.data?.message ||
                'Failed to create WhatsApp trigger';
            toast.error(errorMessage);

        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        console.log('Current robot state:', robot);
    }, [robot]);

    return (
        <div className="trigger-container">
            <Helmet><title>Create WhatsApp Trigger - Autonomie</title></Helmet>
            <h2>Create WhatsApp Trigger</h2>

            <form onSubmit={handleSubmit}>
                {/* Add Trigger Name field at the top */}
                <div className="form-group">
                    <label>Trigger Name *</label>
                    <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className={formErrors.name ? 'error' : ''}
                        placeholder="Enter trigger name"
                        maxLength={255}
                    />
                    {formErrors.name && <span className="error-text">{formErrors.name}</span>}
                </div>

                {/* Phone number field follows */}
                <div className="form-group">
                    <label>WhatsApp Number *</label>
                    <input
                        type="tel"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        className={formErrors.phoneNumber ? 'error' : ''}
                        placeholder="e.g., +905551234567,+905320002233"
                        pattern="^\+[1-9]\d{1,14}(,\+[1-9]\d{1,14})*$"
                    />
                    {formErrors.phoneNumber && <span className="error-text">{formErrors.phoneNumber}</span>}
                </div>
                <small className="hint-text">
                    Use international format: +905551234567,+905320002233 (no spaces)
                </small>
                <div className="form-group">
                    <label>Priority</label>
                    <select
                        value={priority}
                        onChange={(e) => setPriority(e.target.value)}
                    >
                        {priorities.map((p) => (
                            <option key={p.value} value={p.value}>
                                {p.icon} {p.label}
                            </option>
                        ))}
                    </select>
                </div>

                <FolderSelection
                    folder={folder}
                    onChange={setFolder}
                    error={formErrors.folder}
                />

                <RobotSelection
                    robot={robot}
                    onChange={setRobot}
                    folder={folder}
                />
                <ProcessSelection
                    process={process}
                    onChange={setProcess}
                    folder={folder}
                    error={formErrors.process}
                />

                {/* <div className="form-group">
                    <label className="checkbox-label">
                        <input
                            type="checkbox"
                            checked={stickToRobot}
                            onChange={(e) => setStickToRobot(e.target.checked)}
                        />
                        Lock process to selected robot
                    </label>
                    <small className="help-text">
                        When enabled, this process will always run on the selected robot
                    </small>
                </div> */}

                <div className="form-group process-robot-lock">
                    <div className="robot-lock-container">
                        <input
                            type="checkbox"
                            id="stickToRobot"
                            checked={stickToRobot}
                            onChange={(e) => setStickToRobot(e.target.checked)}
                            disabled={robot === 'All'}
                        />
                        <label htmlFor="stickToRobot" className="robot-lock-label">
                            <span className="lock-icon">🔒</span>
                            Lock process to selected robot
                        </label>
                    </div>
                    {robot === 'All' ? (
                        <small className="help-text warning">
                            Select a specific robot to enable process locking
                        </small>
                    ) : (
                        <small className="help-text">
                            This process will only be executed by the selected robot
                        </small>
                    )}
                </div>

                <div className="button-group">
                    <button
                        type="submit"
                        className="submit-button"
                        disabled={isLoading}
                    >
                        {isLoading ? 'Creating...' : 'Create Trigger'}
                    </button>
                    <button
                        type="button"
                        className="cancel-button"
                        onClick={() => navigate('/dashboard')}
                        disabled={isLoading}
                    >
                        Cancel
                    </button>
                </div>
            </form>


        </div>
    );
};

export default WhatsAppTrigger;



